import * as yup from 'yup'
import { noodlesAddressSchema, noodlesRemittanceInstructionsSchema } from './noodles-order.js'
import { MakeRequiredAndNullable } from './utils.js'

export enum NoodlesSupplierOrderLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  unitPrice = 'unitPrice', // price per unit
  itemQty = 'itemQty', // quantity of the item for order
  itemNote = 'itemNote', // note for the item
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const noodlesSupplierOrderFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  supplierOrderNumber: yup.string().nullable().optional(),
  orderNumber: yup.string().nullable().optional(),
  referenceNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  externalNote: yup.string().nullable().optional(),
  customer: yup.object().shape({
    supplierDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        supplierAddress: noodlesAddressSchema.nullable().optional(),
        firstName: yup.string().nullable().optional(),
        lastName: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    vendorDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        vendorAddress: noodlesAddressSchema.nullable().optional(),
        firstName: yup.string().nullable().optional(),
        lastName: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        itemNote: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  taxRate: yup.string().nullable().optional(),
  shippingFee: yup.string().nullable().optional(),
  otherFees: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  remittanceInstructions: noodlesRemittanceInstructionsSchema,
  isNet: yup.string().nullable().optional(),
  showPaymentLink: yup.string().nullable().optional(),
  showBankDetails: yup.string().nullable().optional(),
  paymentBalance: yup.string().nullable().optional(),
})

export type NoodlesSupplierOrderFileData = yup.InferType<typeof noodlesSupplierOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<NoodlesSupplierOrderFileData>

export const noodlesSupplierOrderFileMappings: RequiredFileData = {
  issuer: {
    companyName: '7bb0f306-671f-426e-a333-8d262c3f56e7',
    address: {
      addressLine1: '87345a2d-d548-4306-94c4-25e27bc636f3',
      addressLine2: 'cc5e4414-4f2e-4bc9-ab59-e0664d677570',
      postalCode: '5457a184-c2e7-460d-b2b2-fe7739dd06dc',
      city: '02e2f08e-fd7f-474d-b294-a16bb6f5edff',
      country: '4d66c8c2-43f6-428a-a60b-849c296f46da',
    },
    firstName: 'db4a5ae5-b0af-478f-97a4-794bad67baf4',
    lastName: '4c888031-312b-478e-83dd-528747ddf8f3',
    phoneNumber: 'f7f911ac-bc65-49ce-a7ad-54b0823d8c56',
    emailAddress: '596b6a99-a515-47fb-803f-b54e265a572f',
  },
  supplierOrderNumber: 'f9d66037-ab5f-427f-b2a4-4628340b4ad0',
  orderNumber: 'b6c4c7aa-46b9-4713-9aaf-a1ca9d6430fa',
  referenceNumber: 'ca97837f-bae8-406c-a805-860b467bedee',
  dateIssued: 'b8f629b8-9ea1-4308-b402-55894878e115',
  dateReady: 'b7d8bf32-66b5-465a-a2e7-f933790450f7',
  externalNote: 'e24ea43b-e586-4356-98a0-b36eb2bfc470',
  customer: {
    supplierDetails: {
      firstName: '08188ff2-5dc1-4809-89c1-2980d0a80ab0',
      lastName: '612d4ee0-f2a9-40b8-ba9a-dc11e4cc41a8',
      companyName: '2bbcf2c5-5d9f-4bfa-9d96-a0d21bad7919',
      supplierAddress: {
        addressLine1: 'ad65d509-41da-4bd7-8620-aff9afff71ed',
        addressLine2: 'cc00ab6a-5b7a-4a37-9798-29792531159a',
        postalCode: 'dc6f0824-8588-48a3-b3b2-239c47c81d4b',
        city: '2eadeea1-f53d-4e40-8f39-b63ffac91071',
        country: '9f411bbd-67ff-4c0f-b8e5-89ef4f7659fa',
      },
      phoneNumber: '837c82c7-1be2-44f8-866a-2a0ea27d9d1e',
      emailAddress: 'f4ec5fbc-e0d8-4687-aaad-338f7d8ae515',
    },
    vendorDetails: {
      firstName: '613e8599-392a-407f-9847-3d916e49bdc1',
      lastName: '947a1e40-a5d4-4ced-a707-8c9261cf3bfc',
      companyName: '98cdaad5-675c-48ae-9d7d-a39a115dc69a',
      vendorAddress: {
        addressLine1: 'bfa032b0-9d91-4a4a-8107-ede8bb70474e',
        addressLine2: '839be348-8e14-40b2-9f4f-6d7f7f2906cc',
        postalCode: 'f649eb2a-e99a-4929-b9c8-a43b6b12dda2',
        city: 'e503c75c-0b4a-4ddc-b65c-ef4e8a2aa427',
        country: '0de37ff5-7e7f-4ece-94ef-5f9dd83ff73f',
      },
      phoneNumber: '887fefef-fb80-4a05-9521-392bda326374',
      emailAddress: '9cbcd40d-f986-4c45-bdb5-c9c9d768e9d5',
    },
  },
  items: [
    {
      itemNumber: null,
      itemName: '934f4309-76f8-4406-9de5-04f3a3dfb49b',
      description: '1c8b4c74-52b6-42f8-b1d6-ed3e34a7da85',
      itemQty: '4497699f-504f-4ba4-a017-1237ca36aeda',
      unitPrice: '1f9f5307-bbe4-4491-870d-b56509ff63ba',
      itemNote: 'b494a9ef-3228-45ce-b930-23eb470c9c26',
      lineTotal: null,
    },
  ],
  taxRate: 'c002a88b-dbd1-43df-a286-92a871df1bea',
  shippingFee: '2c322dd5-26e2-4e48-a501-16d46bb5f1ac',
  otherFees: '98d5bb82-9214-4978-8bf5-2b9d68d27a54',
  agreement: 'cfd41523-99cd-49d1-a180-0fff357d37a0',
  instructions: 'f97d3052-4b93-49be-9d5e-e03ae5b765d4',
  remittanceInstructions: {
    bankName: '8924201a-7f6a-49f2-84a9-5da6cf1356e9',
    bankAddress: {
      addressLine1: '192b4018-3afd-4d39-9edb-6a7631e91ea2',
      addressLine2: '8f368690-797f-4ad4-99da-09bc8ab5e4c2',
      postalCode: '2495a820-a0e0-4ecb-8cdb-dcff460a564a',
      city: 'cd332a0f-ae9d-47a0-b831-cc2944088706',
      country: '82cc77c9-9865-4d1c-827a-424a7140313e',
    },
    kto: '5f5582fd-45cc-42e7-a404-29fd9a48ce3f',
    blz: 'e23a5458-6066-4447-bf4b-00c7176c1474',
    iban: 'a7387925-6dad-4263-aca4-3e915db69b1f',
    swift: '50093e4b-9eb9-41ac-81eb-0a62cef707b1',
    firstName: '22a7da55-a3cf-4c21-8cd1-f95e2c921dc7',
    lastName: '4a411bc1-b599-4655-95d1-5690738b715c',
    ustIdNr: '0bb4779e-a68a-4688-8aea-8903b0ece034',
    beneficiaryName: 'fab431f8-9955-4a75-b2cf-fe3c6643190a',
    beneficiaryAddress: {
      addressLine1: '6218788b-a866-4686-af5b-a656ae7ad70f',
      addressLine2: '8953e286-5f12-47b8-8ef0-bf54e015f8f4',
      postalCode: '76729396-dda9-41fb-88dd-ceebb356b26b',
      city: '626d22cc-03e5-45cd-ad14-8561e78d2cf2',
      country: '5e6beacc-cb4c-424d-9d85-ab5355df6842',
    },
    telephone: '271f3f9f-fee0-43d9-955a-8ea6a1ea13a2',
    telefax: '527df3fb-686f-4981-9423-45d62610f124',
    email: '2cb9fb2f-6a4d-419d-8bdd-408c8455e88d',
    website: '9432d241-088c-4b61-9999-e17281488a98',
    paymentLink: '51609924-07b6-41e6-a54c-1b25ba889bf1',
  },
  isNet: '12132c4e-1979-4860-9ef4-78ff86affcf6',
  showPaymentLink: 'fb42f8b0-3e62-4ebf-9a17-212a6a059e33',
  showBankDetails: '45cbb3b3-2989-4c09-a802-0d1838a0c631',
  paymentBalance: 'c631d859-25d7-41e2-95c6-d46528103ca8',
}
